// ============================================================================
// 💬 CHATHEADER — Barra Superior del Chat
// src/features/mensagens/components/chat/ChatHeader.jsx
// ============================================================================

import React, { memo, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Phone,
  Video,
  MoreVertical,
  Star,
  Archive,
  AlertCircle,
} from 'lucide-react';
import { useIsMobile } from '../../../../hooks/useMediaQuery';
import { formatContactName } from '../../utils/formatters';
import { MESSAGE_STATUS } from '../../utils/constants';

const ChatHeader = memo(({
  client,
  conversationStats,
  onBack,
  onCall,
  onStar,
  onArchive,
  onMore,
  isStarred = false,
  isArchived = false,
  isOnline = false,
}) => {
  const isMobile = useIsMobile();

  const displayName = formatContactName(client?.name || 'Desconhecido', 20);
  const initials = (client?.name || '?')
    .split(' ')
    .map(w => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const handleCall = useCallback((e) => {
    e.stopPropagation();
    if (client?.phone) {
      window.open(`tel:${client.phone.replace(/\D/g, '')}`, '_self');
    }
  }, [client]);

  return (
    <div className="flex items-center gap-2 px-3 py-2.5 bg-card border-b border-gray-200 shadow-sm z-10">
      {/* Botón volver */}
      <button
        onClick={onBack}
        className="p-1.5 rounded-full hover:bg-gray-100 text-gray-600 transition-colors flex-shrink-0"
        aria-label="Voltar"
      >
        <ArrowLeft size={20} />
      </button>

      {/* Avatar + Info */}
      <div className="flex items-center gap-2.5 flex-1 min-w-0">
        <div className="relative flex-shrink-0">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
            <span className="text-white font-semibold text-xs">
              {initials}
            </span>
          </div>
          {isOnline && (
            <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white" />
          )}
        </div>

        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <h2 className="font-semibold text-sm text-gray-900 truncate">
              {displayName}
            </h2>
            {isStarred && <Star size={11} className="text-amber-400 fill-amber-400 flex-shrink-0" />}
          </div>
          <p className="text-xs text-gray-400 truncate">
            {isOnline
              ? 'Online'
              : conversationStats
                ? `${conversationStats.total} mensagens`
                : client?.phone
                  ? client.phone
                  : ''}
          </p>
        </div>
      </div>

      {/* Acciones */}
      <div className="flex items-center gap-0.5 flex-shrink-0">
        {/* Llamar */}
        {client?.phone && (
          <button
            onClick={handleCall}
            className="p-2 rounded-full hover:bg-green-50 text-gray-500 hover:text-green-600 transition-colors"
            aria-label="Ligar"
          >
            <Phone size={18} />
          </button>
        )}

        {/* Favorito */}
        <button
          onClick={() => onStar?.(!isStarred)}
          className={`p-2 rounded-full hover:bg-amber-50 transition-colors ${
            isStarred ? 'text-amber-400' : 'text-gray-500 hover:text-amber-500'
          }`}
          aria-label={isStarred ? 'Remover favorito' : 'Favoritar'}
        >
          <Star size={18} className={isStarred ? 'fill-amber-400' : ''} />
        </button>

        {/* Archivar */}
        <button
          onClick={() => onArchive?.(!isArchived)}
          className="p-2 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
          aria-label={isArchived ? 'Desarquivar' : 'Arquivar'}
        >
          <Archive size={18} />
        </button>

        {/* Más */}
        <button
          onClick={onMore}
          className="p-2 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
          aria-label="Mais opções"
        >
          <MoreVertical size={18} />
        </button>
      </div>
    </div>
  );
});

ChatHeader.displayName = 'ChatHeader';
export { ChatHeader };
export default ChatHeader;